import React, { useState } from "react";
import { Card, Tabs, Typography, Space, Row, Col, Button, Steps } from "antd";

const { Title, Paragraph, Text } = Typography;

const ConvolutionalNeuralNetwork = () => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Start: Input Layer",
      description: "The apple image enters as raw pixels into the network.",
      activeLayer: 0,
      showSignal: false,
    },
    {
      title: "Step 1: Detecting Edges",
      description:
        "The first layer, called convolution layer, finds edges and boundaries of the apple.",
      activeLayer: 1,
      showSignal: true,
      fromLayer: 0,
      toLayer: 1,
    },
    {
      title: "Step 2: Detecting Shapes",
      description:
        "Second convolution layer combines edges into shapes and helps recognize complex shapes. This layer recognizes that the input image is a round object",
      activeLayer: 2,
      showSignal: true,
      fromLayer: 1,
      toLayer: 2,
    },
    {
      title: "Step 3: Combining Features",
      description:
        "Dense layer puts all observed features together: round + red + stem = fruit",
      activeLayer: 3,
      showSignal: true,
      fromLayer: 2,
      toLayer: 3,
    },
    {
      title: "Step 4: Final Decision",
      description:
        'Output layer makes the prediction: "Apple!" with 95% confidence',
      activeLayer: 4,
      showSignal: true,
      fromLayer: 3,
      toLayer: 4,
    },
  ];

  const renderConcept = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>What is a CNN?</span>}>
        <Paragraph style={{ fontSize: "16px" }}>
          A Convolutional Neural Network (CNN) is a special type of neural
          network designed to understand images.
        </Paragraph>

        <Paragraph style={{ fontSize: "16px" }}>
          Think about when you look at a picture of an apple 🍎:
        </Paragraph>
        <ul
          style={{
            paddingLeft: "20px",
            fontSize: "16px",
            marginBottom: "16px",
          }}
        >
          <li>
            First, you notice <Text strong>edges</Text> and{" "}
            <Text strong>colors</Text>
          </li>
          <li>
            Then you see <Text strong>round shapes</Text>
          </li>
          <li>Finally, your brain says: "That's an apple!"</li>
        </ul>

        <Paragraph style={{ fontSize: "16px", marginBottom: 0 }}>
          CNNs work the same way - they break down images step by step!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>Key Ideas</span>}>
        <Row gutter={[16, 16]}>
          {[
            {
              title: "Convolution",
              desc: "Finds patterns like edges and shapes",
            },
            {
              title: "Pooling",
              desc: "Makes image smaller but keeps important info",
            },
            {
              title: "Layers",
              desc: "Each layer learns more complex features",
            },
            {
              title: "Classification",
              desc: 'Final decision: "This is an apple!"',
            },
          ].map((item, i) => (
            <Col xs={24} md={12} key={i}>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "flex-start",
                }}
              >
                <div>
                  <Paragraph style={{ fontWeight: 600, marginBottom: "4px" }}>
                    {item.title}
                  </Paragraph>
                  <Paragraph style={{ marginBottom: 0, fontSize: "14px" }}>
                    {item.desc}
                  </Paragraph>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            What About AI Image Generators?
          </span>
        }
      >
        <Paragraph style={{ marginBottom: 0 }}>
          You might have heard about DALL-E, Midjourney, or Stable Diffusion
          that <Text strong>create</Text> images. Those use different technology
          called "Diffusion Models" and "Transformers." CNNs are still excellent
          at <Text strong>understanding</Text> and{" "}
          <Text strong>recognizing</Text> what's in images - like identifying if
          a photo contains a cat, detecting faces, or helping self-driving cars
          see the road!
        </Paragraph>
      </Card>
    </Space>
  );

  const renderDemo = () => {
    const currentStepData = steps[currentStep];
    const layerX = [100, 250, 400, 550, 700];
    const centerY = 200;
    const layers = [
      { count: 3, label: "Input\n(Pixels)", y: centerY },
      { count: 6, label: "Conv 1\n(Edges)", y: centerY },
      { count: 4, label: "Conv 2\n(Shapes)", y: centerY },
      { count: 3, label: "Dense", y: centerY },
      { count: 1, label: "Output", y: centerY },
    ];

    const getNeuronY = (layerIndex, neuronIndex) => {
      const count = layers[layerIndex].count;
      const spacing = 40;
      const startY = centerY - ((count - 1) * spacing) / 2;
      return startY + neuronIndex * spacing;
    };

    const isLayerActive = (layerIdx) => {
      return layerIdx <= currentStepData.activeLayer;
    };

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Card
          title={
            <span style={{ color: "#0071e3" }}>{currentStepData.title}</span>
          }
        >
          <Paragraph style={{ marginBottom: "16px" }}>
            {currentStepData.description}
          </Paragraph>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginBottom: "0px",
              marginTop: "48px",
            }}
          >
            <Button
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
            >
              ← Previous
            </Button>

            <Button disabled>
              Step {currentStep + 1} / {steps.length}
            </Button>

            <Button
              type="primary"
              onClick={() =>
                setCurrentStep(Math.min(steps.length - 1, currentStep + 1))
              }
              disabled={currentStep >= steps.length - 1}
            >
              {currentStep >= steps.length - 1 ? "✓ Complete" : "Next →"}
            </Button>
          </div>

          {/* Network Visualization */}
          <svg
            width="850"
            height="450"
            style={{ display: "block", margin: "0 auto" }}
          >
            {/* Input image */}
            <g>
              <text x="35" y={centerY} textAnchor="middle" fontSize="48">
                🍎
              </text>
              <text
                x="35"
                y={centerY + 30}
                textAnchor="middle"
                fontSize="11"
                fill="#64748b"
                fontWeight="600"
              >
                Input Image
              </text>
            </g>

            {/* Output prediction */}
            {currentStep >= steps.length - 1 && (
              <g>
                <text
                  x="800"
                  y={centerY}
                  textAnchor="middle"
                  fontSize="28"
                  fontWeight="700"
                  fill={currentStep >= steps.length - 1 ? "#52c41a" : "#d9d9d9"}
                >
                  Apple
                </text>
                <text
                  x="800"
                  y={centerY + 30}
                  textAnchor="middle"
                  fontSize="12"
                  fill={currentStep >= steps.length - 1 ? "#52c41a" : "#8c8c8c"}
                  fontWeight="600"
                >
                  {currentStep >= steps.length - 1
                    ? "✓ 95% confident"
                    : "Predicting..."}
                </text>
              </g>
            )}

            {/* Connections */}
            {layers.slice(0, -1).map((layer, layerIdx) => {
              const nextLayer = layers[layerIdx + 1];
              const isCurrentTransition =
                currentStepData.showSignal &&
                currentStepData.fromLayer === layerIdx;

              return Array.from({ length: layer.count }).map((_, i) =>
                Array.from({ length: nextLayer.count }).map((_, j) => (
                  <line
                    key={`conn-${layerIdx}-${i}-${j}`}
                    x1={layerX[layerIdx] + 12}
                    y1={getNeuronY(layerIdx, i)}
                    x2={layerX[layerIdx + 1] - 12}
                    y2={getNeuronY(layerIdx + 1, j)}
                    stroke={isCurrentTransition ? "#0071e3" : "#e2e8f0"}
                    strokeWidth={isCurrentTransition ? "2" : "1"}
                    opacity={isCurrentTransition ? "0.6" : "0.3"}
                  />
                ))
              );
            })}

            {/* Neurons */}
            {layers.map((layer, layerIdx) =>
              Array.from({ length: layer.count }).map((_, neuronIdx) => {
                const x = layerX[layerIdx];
                const y = getNeuronY(layerIdx, neuronIdx);
                const isActive = isLayerActive(layerIdx);
                const isCurrentLayer = layerIdx === currentStepData.activeLayer;

                return (
                  <g key={`neuron-${layerIdx}-${neuronIdx}`}>
                    <circle
                      cx={x}
                      cy={y}
                      r="12"
                      fill="white"
                      stroke={isActive ? "#0071e3" : "#d9d9d9"}
                      strokeWidth="2"
                    />
                    {isCurrentLayer && (
                      <circle
                        cx={x}
                        cy={y}
                        r="12"
                        fill="none"
                        stroke="#0071e3"
                        strokeWidth="2"
                        opacity="0.4"
                      >
                        <animate
                          attributeName="r"
                          values="12;18;12"
                          dur="1.5s"
                          repeatCount="indefinite"
                        />
                        <animate
                          attributeName="opacity"
                          values="0.4;0;0.4"
                          dur="1.5s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    )}
                  </g>
                );
              })
            )}

            {/* Layer labels */}
            {layers.map((layer, idx) => (
              <text
                key={`label-${idx}`}
                x={layerX[idx]}
                y={centerY + layer.count * 20 + 40}
                textAnchor="middle"
                fontSize="11"
                fill={isLayerActive(idx) ? "#0071e3" : "#8c8c8c"}
                fontWeight="600"
              >
                {layer.label.split("\n").map((line, i) => (
                  <tspan key={i} x={layerX[idx]} dy={i === 0 ? 0 : 14}>
                    {line}
                  </tspan>
                ))}
              </text>
            ))}

            {/* Animated signal dots */}
            {currentStepData.showSignal &&
              (() => {
                const fromLayer = currentStepData.fromLayer;
                const toLayer = currentStepData.toLayer;
                const dots = [];

                Array.from({ length: layers[fromLayer].count }).forEach(
                  (_, fromIdx) => {
                    Array.from({ length: layers[toLayer].count }).forEach(
                      (_, toIdx) => {
                        const x1 = layerX[fromLayer] + 12;
                        const y1 = getNeuronY(fromLayer, fromIdx);
                        const x2 = layerX[toLayer] - 12;
                        const y2 = getNeuronY(toLayer, toIdx);

                        dots.push(
                          <circle
                            key={`dot-${fromIdx}-${toIdx}`}
                            cx={x1}
                            cy={y1}
                            r="4"
                            fill="#0071e3"
                            opacity="0.8"
                          >
                            <animate
                              attributeName="cx"
                              values={`${x1};${x2}`}
                              dur="1.5s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="cy"
                              values={`${y1};${y2}`}
                              dur="1.5s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="r"
                              values="4;6;4"
                              dur="1s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        );
                      }
                    );
                  }
                );

                return dots;
              })()}
          </svg>
        </Card>
      </Space>
    );
  };

  const renderHistory = () => {
    const timeline = [
      {
        year: "1980s",
        title: "The Beginning: Learning from Nature",
        event:
          "A scientist named Yann LeCun was curious about how animals see the world. He studied how cat brains process images - when a cat looks at a mouse, certain brain cells react to edges, others to movement, and others to shapes. He thought: 'What if we could make computers work the same way?' So he invented Convolutional Neural Networks, mimicking how animal brains understand vision layer by layer.",
      },
      {
        year: "1998",
        title: "First Real Success: Reading Handwriting",
        event:
          "LeCun and his team created LeNet, a CNN that could read handwritten numbers on envelopes and checks. Before this, postal workers had to read every zip code by hand - imagine sorting millions of letters! LeNet could recognize messy handwriting with amazing accuracy. Banks started using it to read the amounts on checks. This proved CNNs actually worked in the real world, not just in labs.",
      },
      {
        year: "2012",
        title: "The AI Revolution: AlexNet Changes Everything",
        event:
          "A team from the University of Toronto built AlexNet for a huge competition called ImageNet, where computers try to recognize objects in 1.2 million images. AlexNet crushed the competition, making half as many mistakes as the previous best system! This shocked everyone. Suddenly, tech companies realized CNNs were powerful enough to solve real problems. Google, Facebook, and others started investing billions in AI research. This moment launched the deep learning revolution we're living in today.",
      },
      {
        year: "2015-Present",
        title: "CNNs Become Part of Daily Life",
        event:
          "CNNs spread everywhere! Your phone unlocks by recognizing your face. Doctors use them to find diseases in X-rays faster than human experts. Self-driving cars use CNNs to 'see' pedestrians and stop signs. Instagram uses them to detect and blur inappropriate content. They're even helping scientists count endangered animals from satellite photos to protect wildlife. What started as a research experiment is now technology you use every single day without even thinking about it.",
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {timeline.map((item, i) => (
          <Card
            key={i}
            title={
              <span style={{ color: "#0071e3" }}>
                {item.year} - {item.title}
              </span>
            }
          >
            <Paragraph style={{ marginBottom: 0 }}>{item.event}</Paragraph>
          </Card>
        ))}

        <Card
          title={
            <span style={{ color: "#0071e3" }}>Amazing Facts About CNNs</span>
          }
        >
          <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
            <li style={{ marginBottom: "8px" }}>
              <Text strong>Gaming saved AI:</Text> The 2012 breakthrough
              happened because graphics cards (GPUs) designed for video games
              turned out to be perfect for training CNNs. Gamers accidentally
              helped create modern AI!
            </li>
            <li style={{ marginBottom: "8px" }}>
              <Text strong>Inspired by cats:</Text> The whole idea came from
              experiments where scientists showed patterns to cats and measured
              which brain cells activated. Those experiments from the 1950s-60s
              led to CNNs decades later!
            </li>
            <li style={{ marginBottom: 0 }}>
              <Text strong>Better than humans:</Text> In 2015, CNNs became
              better than humans at recognizing objects in photos for the first
              time. They can now identify thousands of different objects with
              incredible accuracy.
            </li>
          </ul>
        </Card>
      </Space>
    );
  };

  const renderUses = () => {
    const uses = [
      {
        category: "Your Phone & Camera",
        examples: [
          "Face unlock and Face ID",
          "Portrait mode (detecting people vs background)",
          "Auto-focus on faces",
          "Photo organization by recognizing people/places",
        ],
      },
      {
        category: "Self-Driving Cars",
        examples: [
          "Detecting pedestrians and cyclists",
          "Reading traffic signs and lights",
          "Lane detection",
          "Obstacle recognition",
        ],
      },
      {
        category: "Medical Imaging",
        examples: [
          "Finding tumors in X-rays and CT scans",
          "Detecting diseases in retina images",
          "Analyzing skin conditions",
          "Identifying broken bones",
        ],
      },
      {
        category: "Social Media",
        examples: [
          "Auto-tagging friends in photos",
          "Content moderation (filtering inappropriate images)",
          "Face filters and effects",
          "Image search",
        ],
      },
      {
        category: "Security & Surveillance",
        examples: [
          "Facial recognition at airports",
          "Detecting suspicious behavior",
          "Monitoring restricted areas",
          "Finding missing persons",
        ],
      },
      {
        category: "Retail & Shopping",
        examples: [
          "Amazon Go cashier-less stores",
          "Visual search (photo of item → find it online)",
          "Inventory counting",
          "Quality control in factories",
        ],
      },
    ];

    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={[16, 16]}>
          {uses.map((use, i) => (
            <Col xs={24} md={12} key={i}>
              <Card
                title={<span style={{ color: "#0071e3" }}>{use.category}</span>}
              >
                <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
                  {use.examples.map((example, j) => (
                    <li key={j}>{example}</li>
                  ))}
                </ul>
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          title={
            <span style={{ color: "#0071e3" }}>
              What About AI Image Generators?
            </span>
          }
        >
          <Paragraph>
            Those tools use newer architectures like{" "}
            <Text strong>Diffusion Models</Text> and{" "}
            <Text strong>Transformers</Text>, not CNNs. Here's the difference:
          </Paragraph>
          <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
            <li style={{ marginBottom: "8px" }}>
              <Text strong>CNNs:</Text> These look at an image and tell what's
              in it (recognition/understanding)
            </li>
            <li style={{ marginBottom: 0 }}>
              <Text strong>Diffusion Models:</Text> These create a new image
              from the given description (generation/creation)
            </li>
          </ul>
        </Card>

        <Card
          title={<span style={{ color: "#0071e3" }}>The Future of CNNs</span>}
        >
          <Paragraph style={{ marginBottom: 0 }}>
            CNNs continue to improve and are being combined with newer
            architectures! They're becoming faster, more accurate, and able to
            work on smaller devices (like your phone). Researchers are also
            making them better at understanding 3D images and video, not just
            static photos.
          </Paragraph>
        </Card>
      </Space>
    );
  };

  const tabItems = [
    {
      key: "concept",
      label: "What is CNN?",
      children: renderConcept(),
    },
    {
      key: "demo",
      label: "See It Work",
      children: renderDemo(),
    },
    {
      key: "history",
      label: "History",
      children: renderHistory(),
    },
    {
      key: "uses",
      label: "Real Uses",
      children: renderUses(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Convolutional Neural Networks (CNNs)</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          How computers learn to see and understand images!
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="concept" items={tabItems} size="large" />
    </div>
  );
};

export default ConvolutionalNeuralNetwork;
